import React from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import UserMenu from "./user-menu";
import { useAuth } from "@/hooks/use-auth";
import { LayoutDashboard, FolderOpen, Building, UserCog, Settings, Tally4 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Tenant } from "@/types";
import { useAuthenticatedRequest } from "@/hooks/use-authenticated-request";

export default function SideNav() {
  const [location] = useLocation();
  const { user: currentUser } = useAuth();
  const authenticatedRequest = useAuthenticatedRequest();

  // Navigation items
  const mainNavItems = [
    { href: "/", icon: LayoutDashboard, label: "Dashboard" },
    { href: "/projects", icon: FolderOpen, label: "Portfolio" },
    { href: "/organizations", icon: Building, label: "Organizations" },
  ];


  const settingsNavItems = [
    { href: "/admin/users", icon: UserCog, label: "User Management" },
    { href: "/settings", icon: Settings, label: "Tenant Management" },
  ];

  // Show Tenant Management for all users (including SuperAdmins)
  // SuperAdmins will need to select a specific tenant in the top-nav first
  const visibleSettingsNavItems = settingsNavItems;

  // Fetch tenants data for SuperAdmin or regular users
  const { data: tenants } = useQuery<Tenant[]>({
    queryKey: ["/api/tenants/list-for-admin", currentUser?.userId],
    queryFn: async () => {
      if (!currentUser) return [];
      
      const res = await authenticatedRequest('/api/tenants/list-for-admin');
      if (!res.ok) {
        console.error("Failed to fetch tenants:", res.statusText);
        return [];
      }
      
      return res.json();
    },
    enabled: !!currentUser?.isSuperAdmin,
  });

  // Fetch tenant management to get tenant name for regular users
  const { data: tenantManagement } = useQuery({
    queryKey: ["/api/tenant/settings", currentUser?.tenantId],
    queryFn: async () => {
      if (!currentUser || currentUser.isSuperAdmin) return null;
      
      const res = await authenticatedRequest('/api/tenant/settings');
      if (!res.ok) {
        console.error("Failed to fetch tenant management:", res.statusText);
        return null;
      }
      
      return res.json();
    },
    enabled: !!currentUser && !currentUser.isSuperAdmin,
  });

  // Check if a nav item is active
  const isActive = (path: string) => {
    if (path === "/" && location === "/") return true;
    if (path !== "/" && location.startsWith(path)) return true;
    return false;
  };

  // Determine what to display in the tenant section
  const getTenantDisplay = () => {
    if (!currentUser) return "Loading...";
    
    // For SuperAdmins, check their active tenant context for display
    if (currentUser.isSuperAdmin) {
      if (currentUser.activeTenantContext === "__ALL__") {
        return "All Tenants";
      } else if (currentUser.activeTenantContext === "null" || !currentUser.activeTenantContext) {
        return "No Tenant Selected";
      }
      // If a specific tenant is selected by SuperAdmin, try to find its name
      // This might require fetching the specific tenant's details if not already available
      // For now, we'll assume tenantId is the name or fallback.
      const selectedTenant = tenants?.find(t => t.tenantId === currentUser.activeTenantContext);
      return selectedTenant?.companyName || currentUser.activeTenantContext || "Full Platform View";
    } else {
      // For regular users, display their actual tenant name
      return tenantManagement?.companyName || "My Tenant";
    }
  };

  // Determine if Tenant Management should be visible
  const shouldShowTenantManagement = () => {
    if (!currentUser) return false;
    
    // SuperAdmins always see Tenant Management
    if (currentUser.isSuperAdmin) return true;
    
    // TenantAdmins always see Tenant Management
    if (currentUser.role === 'TenantAdmin') return true;
    
    // Original logic for SuperAdmins (now redundant due to the check above but kept for clarity if rules change)
    // if (currentUser.isSuperAdmin && currentUser.activeTenantContext && 
    //     currentUser.activeTenantContext !== "__ALL__" && 
    //     currentUser.activeTenantContext !== "null") {
    //   return true;
    // }
    
    return false;
  };

  // Filter visible settings based on user role and context
  const filteredSettingsNavItems = visibleSettingsNavItems.filter(item => {
    // User Management is always visible for SuperAdmins and TenantAdmins
    if (item.label === "User Management") {
      return currentUser?.isSuperAdmin || currentUser?.role === 'TenantAdmin';
    }
    
    // Tenant Management visibility depends on user role and context
    if (item.label === "Tenant Management") {
      return shouldShowTenantManagement();
    }
    
    return true;
  });

  return (
    <aside className="hidden md:flex md:flex-col md:w-64 bg-neutral-800 text-white">
      {/* Logo and Tenant name */}
      <div className="p-4 border-b border-neutral-700">
        <div className="flex items-center">
          <Tally4 className="text-primary text-2xl mr-2" />
          <span className="text-xl font-semibold">Vertrage</span>
        </div>
        <div className="mt-2 pl-1">
          <div className="flex items-center">
            <div className="w-1 h-4 bg-primary rounded-full mr-2"></div>
            <span className="text-sm font-medium text-neutral-300 tracking-wide">
              {getTenantDisplay()}
            </span>
          </div>
        </div>
      </div>
      
      {/* Navigation links */}
      <nav className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-2 py-4">
          <div className="space-y-1">
            {mainNavItems.map((item) => (
              <Link 
                key={item.href} 
                href={item.href}
                className={cn(
                  "flex items-center px-3 py-2 rounded-md text-sm font-medium",
                  isActive(item.href) 
                    ? "bg-primary text-white" 
                    : "text-white hover:bg-neutral-700"
                )}
              >
                <item.icon className="mr-3 text-lg" />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
          
          
          {/* Only show Settings section if there are visible items */}
          {filteredSettingsNavItems.length > 0 && (
            <div className="mt-8">
              <h3 className="px-3 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                Settings
              </h3>
              <div className="mt-2 space-y-1">
                {filteredSettingsNavItems.map((item) => (
                  <Link 
                    key={item.href} 
                    href={item.href}
                    className={cn(
                      "flex items-center px-3 py-2 rounded-md text-sm font-medium",
                      isActive(item.href) 
                        ? "bg-primary text-white" 
                        : "text-white hover:bg-neutral-700"
                    )}
                  >
                    <item.icon className="mr-3 text-lg" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>
      
      {/* User menu */}
      <UserMenu />
    </aside>
  );
}
